import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Calendar, User } from 'lucide-react';
import { circularsAPI } from '../../lib/api.js';
import Card from '../../components/ui/Card.jsx';
import Button from '../../components/ui/Button.jsx';
import Badge from '../../components/ui/Badge.jsx';
import { formatDate } from '../../utils/formatters.js';

function CircularDetailPage() {
  const { id } = useParams();

  const { data: circular, isLoading } = useQuery({
    queryKey: ['circular', id],
    queryFn: () => circularsAPI.getById(id),
  });

  const circularData = circular?.data;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-4"></div>
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
        </div>
        <Card>
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    );
  }

  if (!circularData) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500 dark:text-gray-400">
          Circular not found.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Button variant="secondary" size="sm">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Circulars
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {circularData.title}
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Circular #{circularData.id}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Circular Content
            </h2>
            <div className="prose dark:prose-invert max-w-none">
              <p className="text-gray-900 dark:text-white">
                {circularData.content || 'No content available'}
              </p>
            </div>
          </Card>

          <Card>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Acknowledgments
            </h2>
            <div className="space-y-4">
              {circularData.acknowledgments && circularData.acknowledgments.length > 0 ? (
                circularData.acknowledgments.map((ack) => (
                  <div key={ack.id} className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <User className="h-4 w-4 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {ack.user?.name}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {ack.user?.email}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {formatDate(ack.acknowledgedAt)}
                      </p>
                      <Badge variant="success" size="sm">
                        Acknowledged
                      </Badge>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 dark:text-gray-400">
                  No acknowledgments yet.
                </p>
              )}
            </div>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <Card>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Circular Information
            </h2>
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <Calendar className="h-4 w-4 text-gray-400" />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {formatDate(circularData.createdAt)}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Published Date
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <User className="h-4 w-4 text-gray-400" />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {circularData.author?.name || 'Unknown'}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Author
                  </p>
                </div>
              </div>
            </div>
          </Card>

          <Card>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Actions
            </h2>
            <div className="space-y-3">
              {!circularData.acknowledged && (
                <Button variant="primary" className="w-full">
                  Acknowledge Circular
                </Button>
              )}
              <Button variant="outline" className="w-full">
                Download PDF
              </Button>
              <Button variant="secondary" className="w-full">
                Share Circular
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default CircularDetailPage;
